import React, { FormEvent, useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useIntl } from 'react-intl'
import useGetUserData from '../../../../../Utils/useGetUserData'
import { announceSession } from '../../../../../hooks/queries/useAuth'
import { useVerifyChallenge } from '../../../../../hooks/queries/useTwoFactor'
import { isRateLimited, readTwoFactorError } from '../../../../../lib/twoFactor/errors'
import { useTwoFactorChallengeStore } from '../../../../../store/twoFactorChallengeStore'
import { CodeField } from './CodeField'
import { clockFormat, useCountdown } from './useCountdown'

const LOGIN_PATH = '/auth/login'

/**
 * Verify screen (public) — shown between the password and the app for a user
 * who already has an authenticator.
 *
 * The challenge lives in memory only (`twoFactorChallengeStore`), so a reload
 * lands here with nothing and is sent back to the password step. One input takes
 * a 6-digit code or a recovery code. A wrong code keeps the user here with an
 * inline error; `challenge_invalid` (expired, spent, burned after 5 wrong codes)
 * returns them to sign-in.
 */
export const TwoFactorVerify: React.FC = () => {
  const intl = useIntl()
  const t = (id: string, values?: Record<string, string>) => intl.formatMessage({ id: `TWO_FACTOR.${id}` }, values)
  const navigate = useNavigate()
  const getUserData = useGetUserData()

  const challenge = useTwoFactorChallengeStore((s) => s.challenge)
  const endChallenge = useTwoFactorChallengeStore((s) => s.end)
  const verify = useVerifyChallenge()

  const [code, setCode] = useState('')
  const [error, setError] = useState<string | undefined>()
  const leaving = useRef(false)

  const active = challenge?.kind === 'verify' ? challenge : null
  const remaining = useCountdown(active?.expiresAt ?? null)

  const backToSignIn = (notice?: 'expired') => {
    leaving.current = true
    endChallenge(notice)
    navigate(LOGIN_PATH, { replace: true })
  }

  // A reload (or a direct visit) has no challenge: start again from the password.
  useEffect(() => {
    if (leaving.current || active) return
    navigate(LOGIN_PATH, { replace: true })
  }, [active, navigate])

  // The token's 300 s lifetime ran out while the screen was open.
  useEffect(() => {
    if (remaining === 0 && !leaving.current) backToSignIn('expired')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remaining])

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!active || verify.isPending) return
    const trimmed = code.trim()
    if (!trimmed) {
      setError(t('verify.errors.required'))
      return
    }
    setError(undefined)
    verify.mutate(
      { challenge_token: active.token, code: trimmed },
      {
        onSuccess: () => {
          leaving.current = true
          announceSession()
          endChallenge()
          // The session cookie is set: loading the profile flips the router into the app.
          getUserData()
        },
        onError: (err) => {
          const failure = readTwoFactorError(err)
          if (failure.code === 'challenge_invalid') {
            backToSignIn('expired')
            return
          }
          setCode('')
          setError(
            failure.code === 'invalid_code'
              ? t('verify.errors.invalidCode')
              : isRateLimited(failure)
                ? t('errors.rateLimited')
                : t('errors.generic'),
          )
        },
      },
    )
  }

  if (!active) return null

  return (
    <div className='card'>
      <div className='px-md-5 py-5 card-body'>
        <div className='d-flex flex-column justify-content-center align-items-center mb-8 text-center'>
          <h1>{t('verify.title')}</h1>
          <span className='opacity-75'>{t('verify.subtitle')}</span>
        </div>
        <form onSubmit={submit} noValidate id='two_factor_verify_form'>
          <CodeField value={code} onChange={setCode} error={error} disabled={verify.isPending} autoFocus />
          <button type='submit' className='btn-block w-100 btn btn-lg btn-primary' disabled={verify.isPending}>
            {verify.isPending ? (
              <span className='indicator-progress' style={{ display: 'block' }}>
                {t('verify.pleaseWait')}
                <span className='spinner-border spinner-border-sm align-middle ms-2' />
              </span>
            ) : (
              t('verify.submit')
            )}
          </button>
        </form>
        {remaining !== null && (
          <p className='mt-4 mb-0 text-muted fs-7 text-center' role='timer' aria-live='off'>
            {t('verify.expiresIn', {
              time: clockFormat(remaining, (n) => intl.formatNumber(n)),
            })}
          </p>
        )}
        <div className='mt-6 text-center'>
          <p className='text-muted fs-7 mb-2'>{t('verify.lostDevice')}</p>
          <Link to={LOGIN_PATH} onClick={() => backToSignIn()}>
            {t('backToSignIn')}
          </Link>
        </div>
      </div>
    </div>
  )
}
